import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { defaultCameraUrl, videoSpike, type VideoSpikeMode, type VideoSpikeState } from './video_spike.ts';

const field: CSSProperties = {
    background: '#1c1c1c',
    color: 'inherit',
    border: '1px solid #444',
    borderRadius: 4,
    padding: '6px 8px',
    font: 'inherit',
};

const MODES: readonly VideoSpikeMode[] = ['auto', 'worker', 'worker-main-paint', 'main'];

function duration(ms: number) : string {
    const seconds = Math.floor(ms / 1000);
    const pad = (value: number) => String(value).padStart(2, '0');

    return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}`;
}

const clock = (time: number) : string => new Date(time).toTimeString().slice(0, 8);

function overlay({ status, error, stats }: VideoSpikeState) : string {
    const lines = [error ? `${status} (last problem: ${error})` : status];
    if (!stats) {
        return lines.join('\n');
    }

    const { decoder, depacketizer, session } = stats;
    lines.push(
        `pipeline: ${stats.mode ?? 'starting'}` + (stats.modeFallbackReason ? ` (${stats.modeFallbackReason})` : ''),
        `up ${duration(stats.uptimeMs)}, sessions ${stats.sessions}, stalls ${stats.stalls}, failures ${stats.failures}, ` +
            `${stats.framesDecodedTotal} pictures in all`,
    );
    if (decoder) {
        if (decoder.config) {
            const { codec, codedWidth, codedHeight, hardwareAcceleration } = decoder.config;
            lines.push(`${codec} ${codedWidth}x${codedHeight}, asked for ${hardwareAcceleration}`);
        }
        lines.push(
            `decode: ${decoder.acceleration ?? 'no picture yet'}` +
                (decoder.frameFormat ? `, frames ${decoder.frameFormat} ${decoder.displayWidth}x${decoder.displayHeight}` : '') +
                (decoder.softwareFallbackReason ? ` (hardware refused: ${decoder.softwareFallbackReason})` : ''),
            `${decoder.fps} fps, ${decoder.framesDecoded} decoded, ${decoder.droppedAccessUnits} dropped by decoder, queue ${decoder.decodeQueueSize}`,
            `arrival to picture: ${decoder.latencyMs === null ? '-' : decoder.latencyMs.toFixed(1)} ms, ` +
                `max ${decoder.latencyMaxMs === null ? '-' : decoder.latencyMaxMs.toFixed(0)} ms`,
            `skips to key picture ${decoder.skipsToKeyFrame}, decoder errors ${decoder.decoderErrors}` +
                (decoder.lastError ? ` (${decoder.lastError})` : ''),
        );
    }
    if (depacketizer) {
        lines.push(
            `rtp: ${depacketizer.packets} packets, ${depacketizer.lostPackets} lost, ${depacketizer.droppedAccessUnits} pictures dropped, ` +
                `${depacketizer.keyFrames} key pictures`,
        );
    }
    if (session) {
        lines.push(
            `session ${duration(session.playingMs)}, ${((session.rtpBytes * 8) / Math.max(session.playingMs, 1) / 1000).toFixed(2)} Mbit/s average` +
                (session.firstFrameMs === null ? '' : `, first picture after ${session.firstFrameMs.toFixed(0)} ms`),
        );
    }
    for (const event of stats.events.slice(-4)) {
        lines.push(`${clock(event.time)} ${event.type}${event.detail ? `: ${event.detail}` : ''}`);
    }
    return lines.join('\n');
}

/** Spike page of iterations I2 and I3: one RTSP stream on a canvas with a text overlay. */
export function VideoSpikePanel() : ReactNode {
    const [url, setUrl] = useState('');
    const [mode, setMode] = useState<VideoSpikeMode>('auto');
    const [state, setState] = useState<VideoSpikeState>(() => videoSpike.state());

    useEffect(() => {
        const fillInCamera = async () : Promise<void> => {
            const found = await defaultCameraUrl();
            setUrl((current) => current || (found ?? ''));
        };
        void fillInCamera();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setState(videoSpike.state()), 500);

        return () => {
            clearInterval(timer);
            void videoSpike.stop();
        };
    }, []);

    const busy = state.status !== 'idle' && state.status !== 'error' && state.status !== 'stopped';

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <input
                    style={{ ...field, flex: '1 1 320px' }}
                    aria-label="RTSP URL"
                    value={url}
                    onChange={(event) => setUrl(event.target.value)}
                />
                <select
                    style={field}
                    aria-label="Pipeline"
                    value={mode}
                    disabled={busy}
                    onChange={(event) => setMode(event.target.value as VideoSpikeMode)}
                >
                    {MODES.map((value) => (
                        <option key={value}>{value}</option>
                    ))}
                </select>
                <button style={field} onClick={() => void (busy ? videoSpike.stop() : videoSpike.start({ url, mode }))}>
                    {busy ? 'Stop' : 'Play'}
                </button>
            </div>
            <div style={{ position: 'relative', maxWidth: 1024, minHeight: 120, background: '#000' }}>
                {/* The canvas is put here by videoSpike, outside React's control. */}
                <div ref={videoSpike.setHost} />
                <pre
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        margin: 0,
                        padding: '6px 8px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        font: '12px/1.4 monospace',
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {overlay(state)}
                </pre>
            </div>
        </div>
    );
}
